import { useLayoutEffect, useRef, useState } from "react";

/**
 * Phones (below 768) show a list/detail page as one or the other, never both stacked: tapping a
 * row opens its detail in place of the list, and an on-screen Back returns to the list at the
 * scroll position it was left at. Tablets and wider keep the side-by-side layout untouched
 * (showList and showDetail are both true there, and open/back do nothing).
 *
 * The open state is separate from the page's own selection on purpose: AdminFlights selects its
 * first flight on load, and keying the phone view on "something is selected" would skip the list.
 *
 * Back is an on-screen button only, no URL state (Travis, 2026-10-09). Known trade-off: a phone's
 * swipe-back gesture leaves the page instead of returning to the list.
 */
export function usePhoneDetail(isPhone: boolean) {
  const [open, setOpen] = useState(false);
  const listScrollY = useRef(0);
  const pending = useRef<"top-of-detail" | "list" | null>(null);
  const detailRef = useRef<HTMLDivElement | null>(null);

  // Runs after the swap renders and before paint, so the user never sees the wrong scroll spot.
  useLayoutEffect(() => {
    const target = pending.current;
    pending.current = null;
    if (target === "list") window.scrollTo(0, listScrollY.current);
    if (target === "top-of-detail" && detailRef.current) {
      window.scrollTo(0, Math.max(0, detailRef.current.getBoundingClientRect().top + window.scrollY - 16));
    }
  }, [open]);

  const phoneOpen = isPhone && open;
  return {
    showList: !phoneOpen,
    showDetail: !isPhone || open,
    /** True only on a phone with the detail showing: render the Back button. */
    phoneOpen,
    detailRef,
    openDetail: () => {
      if (!isPhone || open) return;
      listScrollY.current = window.scrollY;
      pending.current = "top-of-detail";
      setOpen(true);
    },
    back: () => {
      if (!open) return;
      pending.current = "list";
      setOpen(false);
    },
  };
}
